import { useCallback, useEffect, useRef, useState } from "react";
import qapi from "views_shared/app/javascript/api";
import qlog from "views_shared/app/javascript/scripts/qlog";

/**
 * useFollowControl — data half of the shared follow/unfollow control.
 *
 * Tracks whether the signed-in viewer's profile follows one followable and exposes an
 * optimistic toggle. The engine supplies the followable (a Rails class name + id, opaque
 * to this hook); the viewer's own profile comes from the session bridge
 * (`globalThis.Qwoted.CURRENT_PROFILE_ID`), so there is nothing to render for an
 * anonymous viewer — `enabled` is false and the chrome half returns null. The whole control
 * is also hidden unless the server exports `PROFILE_FOLLOW_ENABLED` (off by default).
 *
 * "Unfollow" writes follow_status "mute", not a delete: the follows endpoint has no
 * destroy, and mute is the established un-follow idiom (see the hashtag Following page).
 *
 * @param {Object} options
 * @param {string} options.followableType - Rails class name of the thing to follow
 *   (e.g. "Id::Profile::Promoter"); forwarded to the API unexamined
 * @param {number|string} [options.followableId] - Numeric id of the thing to follow.
 *   The hook idles until this is set, so pass the id from the loaded row
 * @returns {{ enabled: boolean, isLoading: boolean, isFollowing: boolean,
 *   isMutating: boolean, toggle: Function }}
 */
function useFollowControl({ followableType, followableId = null }) {
  const viewerProfileId = globalThis.Qwoted?.CURRENT_PROFILE_ID;
  const featureEnabled = Boolean(globalThis.Qwoted?.PROFILE_FOLLOW_ENABLED);
  const enabled = Boolean(featureEnabled && viewerProfileId && followableType && followableId);

  const [isLoading, setIsLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  // Synchronous double-click guard: setIsMutating commits too late to stop a same-tick
  // second click (the MessageButton send guard, same reason).
  const mutatingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    let componentIsMounted = true;

    const load = async () => {
      setIsLoading(true);
      try {
        const response = await qapi.follows.getMine({
          profileId: viewerProfileId,
          followableType,
          followableId,
        });
        if (!componentIsMounted) return;

        // A failed read (resolved `{ error }` or a thrown network error, see catch) degrades to
        // the "Follow" default rather than leaving the button stuck disabled; the write upserts.
        if (response.error) qlog.warn("Follow-state lookup failed:", response.error);
        const row = Array.isArray(response.data) ? response.data[0] : null;
        setIsFollowing(row?.follow_status === "follow");
      } catch (error) {
        if (!componentIsMounted) return;
        qlog.warn("Follow-state lookup failed:", error);
        setIsFollowing(false);
      } finally {
        if (componentIsMounted) setIsLoading(false);
      }
    };
    load();

    return () => {
      componentIsMounted = false;
    };
  }, [enabled, viewerProfileId, followableType, followableId]);

  const toggle = useCallback(async () => {
    if (!enabled || mutatingRef.current) return;
    mutatingRef.current = true;
    setIsMutating(true);

    const next = !isFollowing;
    setIsFollowing(next); // optimistic; rolled back on error

    try {
      await qapi.follows.setStatus({
        profileId: viewerProfileId,
        followableType,
        followableId,
        followStatus: next ? "follow" : "mute",
      });
    } catch (error) {
      setIsFollowing(!next);
      qlog.error("Failed to update follow status:", error);
      globalThis.Qwoted?.showToast?.({
        toastTitle: "Error",
        toastBody: "Couldn't update follow — please try again.",
        variant: "danger",
      });
    } finally {
      mutatingRef.current = false;
      setIsMutating(false);
    }
  }, [enabled, isFollowing, viewerProfileId, followableType, followableId]);

  return { enabled, isLoading, isFollowing, isMutating, toggle };
}

export default useFollowControl;
